import type { SabClientListItemDto } from "Infrastructure/Api/generated/types.gen";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
	getSabClientIdentificationNumber,
	SabClientPicker,
} from "Modeling/Components/SabClientPicker";
import { ArrowLeft, User, UserPlus } from "Shared/appIcons";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

export type LaunchModeSelection =
	| { mode: "without-client" }
	| {
			mode: "with-client";
			sabClientID: number;
			sabIdentificationNumber: string;
	  };

type LaunchModeModalProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onLaunch: (selection: LaunchModeSelection) => void;
	/**
	 * Fires alongside `onLaunch` with the full SAB row when a client is picked.
	 * Optional — used by callers that need the display name (e.g. the global
	 * client switcher in the page header).
	 */
	onPickClient?: (client: SabClientListItemDto) => void;
	title: string;
	description?: string;
	isLaunching?: boolean;
};

export const LaunchModeModal = ({
	open,
	onOpenChange,
	onLaunch,
	onPickClient,
	title,
	description,
	isLaunching,
}: LaunchModeModalProps) => {
	const { t } = useResource();
	const [step, setStep] = useState<"choice" | "pick-client">("choice");

	// Reset step on close so reopening always lands on "choice" instead of
	// continuing the prior session's picker view.
	const handleOpenChange = (next: boolean) => {
		if (!next) setStep("choice");
		onOpenChange(next);
	};

	const handlePick = (client: SabClientListItemDto) => {
		const identificationNumber = getSabClientIdentificationNumber(client);
		if (!identificationNumber) {
			// Defensive: SAB always returns at least one of personal/company ID; if neither,
			// the BE upsert would fail validation. Refuse to advance.
			return;
		}
		onPickClient?.(client);
		onLaunch({
			mode: "with-client",
			sabClientID: client.sabClientID,
			sabIdentificationNumber: identificationNumber,
		});
	};

	const handleWithoutClient = () => onLaunch({ mode: "without-client" });

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{description ? (
						<DialogDescription>{description}</DialogDescription>
					) : null}
				</DialogHeader>

				{step === "choice" ? (
					<div className="flex flex-col gap-3 pt-2">
						<Button
							type="button"
							variant="default"
							className="h-auto w-full justify-start gap-3 px-4 py-3 text-left"
							onClick={() => setStep("pick-client")}
							disabled={isLaunching}
						>
							<UserPlus className="size-5 flex-shrink-0" />
							<div className="flex-1">
								<div className="text-body font-semibold">
									{t(Resources.Modeling.LaunchMode.WithClientButton)}
								</div>
								<div className="text-fine opacity-90">
									{t(Resources.Modeling.LaunchMode.WithClientHint)}
								</div>
							</div>
						</Button>
						<Button
							type="button"
							variant="outline"
							className="h-auto w-full justify-start gap-3 px-4 py-3 text-left"
							onClick={handleWithoutClient}
							disabled={isLaunching}
						>
							<User className="size-5 flex-shrink-0" />
							<div className="flex-1">
								<div className="text-body font-semibold">
									{t(Resources.Modeling.LaunchMode.WithoutClientButton)}
								</div>
								<div className="text-fine text-muted-foreground">
									{t(Resources.Modeling.LaunchMode.WithoutClientHint)}
								</div>
							</div>
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-3 pt-1">
						<SabClientPicker onPick={handlePick} />
						<div className="flex items-center justify-between gap-2 border-t border-border pt-3">
							<Button
								type="button"
								variant="ghost"
								onClick={() => setStep("choice")}
								disabled={isLaunching}
							>
								<ArrowLeft className="size-4" />
								{t(Resources.Modeling.LaunchMode.BackToChoice)}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={handleWithoutClient}
								disabled={isLaunching}
							>
								{t(Resources.Modeling.LaunchMode.ContinueWithoutClient)}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};
